import {
  AutoComplete,
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  TimePicker,
  Image,
  Spin,
} from 'antd';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../stores/root-store-context';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import TextArea from 'antd/es/input/TextArea';
import { getDateLocale } from '../../helpers/dateLocalization';
import CPEWarning from './CPEWarning';
import { StarFilled } from '@ant-design/icons';
import { DateSort } from '../../helpers/SorterHelper';
import { Roles } from '../../enums/Roles';

interface Props {
  isModalOpen: boolean;
  hideModal: () => void;
  projectId?: string;
  userId?: string;
  isChangeUser: boolean;
  refresh: boolean;
  setRefresh: (v: boolean) => void;
}

export const AddTimeReport: React.FC<Props> = observer(
  ({
    isModalOpen,
    hideModal,
    projectId,
    userId,
    isChangeUser,
    refresh,
    setRefresh,
  }) => {
    const { applicationStore, timeReportStore, employeesStore, projectsStore, favoriteProjectsStore } =
      useStore();
    const [isLoading, setIsLoading] = useState(false);

    const currentUser = applicationStore.user;
    if (!currentUser) return null;

    const { t, i18n } = useTranslation();
    const [lang, setLang] = useState(i18n.language);
    const dateLocale = getDateLocale(i18n.language);
    const [form] = Form.useForm();
    const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
    const [inputSearchValue, setInputSearchValue] = useState("");

    useEffect(() => setLang(i18n.language), [i18n.language]);

    useEffect(() => {
      if (!isModalOpen) return;

      if (!projectId) {
        console.log(JSON.stringify(timeReportStore.reports))
        const report: any = timeReportStore.reports.sort((a, b) => DateSort(a.endTime, b.endTime)).find((r: any) => r.log ? r.log?.userId === userId : r?.userId === userId);
        projectId = report?.log ? report?.log?.projectId : report?.projectId;
      }
      form.setFieldsValue({
        user: `${employeesStore.users.find(u => u.id === userId)?.firstName || ''} ${employeesStore.users.find(u => u.id === userId)?.lastName || ''}`,
        userId,
        project: projectsStore.projects.find(p => p.id === projectId)?.title,
        projectId,
        endTime: dayjs(),
      });
      const title = projectsStore.projects.find(p => p.id === projectId)?.title;
      if (title && !form.getFieldValue('description')) {
        form.setFieldValue('description', t('WorkOnProject', { projectTitle: title }));
      }
      setSelectedDate(dayjs());
    }, [isModalOpen, projectId, userId, lang]);

    const hasFinanceAccess = currentUser.roles?.some(r =>
      [Roles.Owner, Roles.Financier].includes(r.id)
    ) ?? false;

    const submit = async () => {
      const { projectId, time, endTime, userId, description } =
        form.getFieldsValue();
      const timeStr = dayjs(time).format('HH:mm');
      const dateStr = dayjs(endTime).format('YYYY-MM-DD');
      setIsLoading(true);
      await timeReportStore.add(projectId, timeStr, dateStr, userId, description);
      await timeReportStore.getMyTime(currentUser.id);
      if (hasFinanceAccess) {
        await timeReportStore.getForFinancier(true);
        setRefresh(!refresh);
      } else {
        await timeReportStore.getAllByPeriod(true);
        setRefresh(!refresh);
      }
      form.resetFields();
      hideModal();
      setIsLoading(false);
    };

    const allReports = hasFinanceAccess
      ? (timeReportStore.reportsForFinancier ?? [])
      : (timeReportStore.reports ?? []);

    return (
      <Modal
        key={`${projectId}-${userId}-${lang}`}
        open={isModalOpen}
        destroyOnClose
        footer={null}
        onCancel={() => {
          form.resetFields();
          hideModal();
        }}
        title={t('LogTime')}
      >
        <Spin spinning={isLoading}>
          <CPEWarning
            selectedDate={selectedDate}
            timeReports={allReports}
            userId={userId ?? currentUser.id}
          />

          <Form
            key={lang}
            form={form}
            onFinish={submit}
            initialValues={{
              time: dayjs(`${applicationStore.company.HoursVacation}:00`, "HH:mm"),
              endTime: dayjs(),
            }}
            labelCol={{ span: 6 }}
            labelAlign="left"
          >
            <Form.Item name="projectId" hidden><Input /></Form.Item>
            <Form.Item name="userId" hidden><Input /></Form.Item>

            <Form.Item label={t('Project')} name="project" rules={[{ required: true }]}>
              <AutoComplete
                style={{ height: 40 }}
                placeholder="Project"
                options={projectsStore.projects
                  .filter(
                    (project) =>
                    // project.isActive !== false &&
                    (project.isPublish ||
                      project.developers.some(d => d.id === currentUser.id)))
                  .sort((a, b) => {
                    const aFav = favoriteProjectsStore.ids.has(Number(a.id));
                    const bFav = favoriteProjectsStore.ids.has(Number(b.id));
                    return (aFav === bFav) ? 0 : aFav ? -1 : 1;
                  })
                  ?.map((el) => ({
                    value: el.title,
                    label: (
                      <Flex justify="space-between" align="center" style={{ width: '100%', padding: "5px" }}>
                        <span>{el.title}</span>
                        {favoriteProjectsStore.ids.has(Number(el.id)) && (
                          <StarFilled style={{ color: '#fadb14', fontSize: 16 }} />
                        )}
                      </Flex>
                    ),
                    data: el,
                  }))}
                onChange={(value, option: any) => {
                  form.setFieldValue("projectId", option.data?.id);

                  const description = form.getFieldValue("description");

                  if (option?.data?.title && !description || description === "") {
                    form.setFieldValue(
                      "description",
                      t("WorkOnProject", { projectTitle: option.data.title || "" })
                    );
                  }
                }}
                searchValue={inputSearchValue}
                onSearch={(text) => setInputSearchValue(text)}
                filterOption={(inputValue, option) => {

                  return (
                    option?.value
                      .toUpperCase()
                      .indexOf(inputValue.toUpperCase()) !== -1
                  );
                }}

              />
            </Form.Item>

            <Form.Item
              hidden={!hasFinanceAccess}
              label={t('User')}
              name="user"
              rules={[{ required: true }]}
            >
              <AutoComplete
                disabled={!isChangeUser}
                placeholder={t('User')}
                options={employeesStore.users.map(u => ({
                  value: `${u.firstName} ${u.lastName}`,
                  data: u,
                  label: (
                    <Flex gap={5}>
                      <Image
                        width={40}
                        height={40}
                        preview={false}
                        style={{ borderRadius: '50%', objectFit: 'cover' }}
                        src={
                          u.photoId
                            ? `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/w_300/${u.photoId}`
                            : 'https://res.cloudinary.com/dnl3x07wo/image/upload/w_300/z03w7hlsy8hcoic9dlcn'
                        }
                      />
                      <div>
                        <div>{u.firstName} {u.lastName}</div>
                        <div>{u.email}</div>
                      </div>
                    </Flex>
                  ),
                }))}
                onChange={(v, o: any) => form.setFieldValue('userId', o?.data?.id)}
              />
            </Form.Item>

            <Form.Item label={t('Time')} name="time" rules={[{ required: true }]}>
              <TimePicker
                defaultValue={dayjs("00:00", "HH:mm")}
                format={"HH:mm"}
                onCalendarChange={(date: any) => {
                  form.setFieldValue("time", date);
                }}
              />
            </Form.Item>

            <Form.Item
              label={t('Date')}
              name="endTime"
              rules={[{ required: true }]}
            >
              <DatePicker
                key={i18n.language}
                locale={dateLocale}
                defaultValue={form.getFieldValue("endTime")}
                onCalendarChange={(date: any) => {
                  form.setFieldValue("date", date);
                }}
              />
            </Form.Item>

            <Form.Item label={t('Description')} name="description">
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="theme-btn"
                style={{ height: 65, float: 'right' }}
              >
                {t('Add')}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    );
  }
);
